import { baseUrl } from 'api/config'

export interface AgentMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface UsageStats {
  turns: number
  inputTokens: number
  outputTokens: number
  cacheWriteTokens: number
  cacheReadTokens: number
  actualCost: number
  uncachedCost: number
  savedCost: number
}

export interface AgentChatCallbacks {
  onProgress?: (step: string) => void
  onDone?: (response: string) => void
  onError?: (error: string) => void
  onUsage?: (usage: UsageStats) => void
  onPayloadSize?: (bytes: number) => void
}

export interface RouteContext {
  page: 'deal' | 'organization' | 'other'
  PK?: string
  SK?: string
  tab?: string
}

/**
 * Parse the current pathname into a RouteContext so the agent
 * knows what entity the user is currently viewing.
 */
export function parseRouteContext(pathname: string): RouteContext {
  // Deal page: /deals/ORG_xxx/DEAL_yyy/agreement
  const dealMatch = pathname.match(/^\/deals\/(ORG_[^/]+)\/(DEAL_[^/]+)(?:\/([^/]+))?/)
  if (dealMatch) {
    return { page: 'deal', PK: dealMatch[1], SK: dealMatch[2], tab: dealMatch[3] }
  }

  // Organization page: /organizations/ORG_xxx/details
  const orgMatch = pathname.match(/^\/organizations\/(ORG_[^/]+)(?:\/([^/]+))?/)
  if (orgMatch) {
    return { page: 'organization', PK: orgMatch[1], tab: orgMatch[2] }
  }

  return { page: 'other' }
}

/**
 * Send messages to the agent via SSE (streaming).
 * Returns a cancel function — call it to abort the in-flight request.
 * The abort is silent: no error message is added to the chat thread.
 */
export function sendAgentMessage(
  messages: AgentMessage[],
  callbacks: AgentChatCallbacks,
  routeContext?: RouteContext,
  model?: string
): () => void {
  const controller = new AbortController()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')

  ;(async () => {
    try {
      const body = JSON.stringify({ messages, currentUser, routeContext, model })
      callbacks.onPayloadSize?.(new Blob([body]).size)

      const response = await fetch(`${baseUrl}/api/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        callbacks.onError?.(`Request failed: ${response.status}`)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event = JSON.parse(line.slice(6))

            if (event.type === 'progress') {
              callbacks.onProgress?.(event.step)
            } else if (event.type === 'done') {
              if (event.usage) callbacks.onUsage?.(event.usage)
              callbacks.onDone?.(event.response)
            } else if (event.type === 'error') {
              callbacks.onError?.(event.error)
            }
          } catch {
            // Skip malformed lines
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return // silently cancelled
      callbacks.onError?.(err?.message ?? 'Unknown error')
    }
  })()

  return () => controller.abort()
}
